import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { View, Pressable, useWindowDimensions, Modal, Platform } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { ThemeContext } from '../utility/context/ThemeContext'

type AnchorRect = {
  x: number
  y: number
  width: number
  height: number
}

type PopoverAppearance = {
  backgroundColor?: string
  borderRadius?: number
  borderColor?: string
  borderWidth?: number
}

type PopoverShowOptions = {
  anchor: AnchorRect
  content: React.ReactNode
  onClose?: () => void
  offset?: number | { x: number, y: number }
  appearance?: PopoverAppearance
  align?: 'left' | 'center' | 'right' | 'auto'
  vertical?: 'above' | 'below' | 'auto'
  showArrow?: boolean
}

type PopoverContextType = {
  showFromAnchor: (options: PopoverShowOptions) => void
  hide: () => void
}

const PopoverContext = createContext<PopoverContextType | undefined>(undefined)

export const PopoverProvider = (
  {
    children,
    defaultOffset,
    defaultAppearance
  }: {
    children: React.ReactNode,
    defaultOffset?: number | { x: number, y: number },
    defaultAppearance?: PopoverAppearance
  }
): React.ReactElement => {
  const { theme } = useContext(ThemeContext)
  const { width: windowWidth, height: windowHeight } = useWindowDimensions()
  const insets = useSafeAreaInsets()

  const [visible, setVisible] = useState(false)
  const [anchor, setAnchor] = useState<AnchorRect | null>(null)
  const [content, setContent] = useState<React.ReactNode>(null)
  const onCloseRef = useRef<(() => void) | undefined>(undefined)

  const [contentSize, setContentSize] = useState<{ width: number; height: number }>(
    { width: 0, height: 0 }
  )
  const [currentOffset, setCurrentOffset] = useState<{ x: number, y: number }>(
    { x: 0, y: 0 }
  )
  const [currentAppearance, setCurrentAppearance] = useState<PopoverAppearance | undefined>(
    undefined
  )
  const [currentAlign, setCurrentAlign] = useState<'left' | 'center' | 'right'>('left')
  const [currentVertical, setCurrentVertical] = useState<'above' | 'below'>('below')
  const [currentShowArrow, setCurrentShowArrow] = useState<boolean>(false)
  const anchorRectRef = useRef<AnchorRect | null>(null)
  const contentRectRef = (
    useRef<{ x: number, y: number, width: number, height: number } | null>(null)
  )
  const mouseMoveHandlerRef = useRef<((e: any) => void) | null>(null)

  const hide = useCallback(() => {
    setVisible(false)
    setTimeout(() => {
      setAnchor(null)
      setContent(null)
      const cb = onCloseRef.current
      onCloseRef.current = undefined
      if (cb) cb()
    }, 0)
  }, [])

  const showFromAnchor = useCallback(({
    anchor,
    content,
    onClose,
    offset,
    appearance,
    align,
    vertical,
    showArrow
  }: PopoverShowOptions) => {
    setAnchor(anchor)
    setContent(content)
    onCloseRef.current = onClose
    anchorRectRef.current = anchor

    const toXY = (o?: number | { x: number, y: number }): { x: number, y: number } => {
      if (typeof o === 'number') return { x: 0, y: o }
      if (o && typeof o === 'object') return { x: o.x ?? 0, y: o.y ?? 0 }
      if (typeof defaultOffset === 'number') return { x: 0, y: defaultOffset }
      if (defaultOffset && typeof defaultOffset === 'object') {
        return {
          x: defaultOffset.x ?? 0,
          y: defaultOffset.y ?? 0
        }
      }
      return { x: 0, y: 0 }
    }

    setCurrentOffset(toXY(offset))
    setCurrentAppearance(appearance || defaultAppearance)
    // Auto-align based on anchor horizontal position within the window:
    // - Leftmost 1/5 of screen -> align 'right' (bubble opens to the right of anchor)
    // - Rightmost 1/5 of screen -> align 'left'
    // - Otherwise center
    const anchorCenterX = anchor.x + (anchor.width / 2)
    const leftThreshold = windowWidth * 0.20
    const rightThreshold = windowWidth * 0.80
    const computedAlign: 'left' | 'center' | 'right' =
      align && align !== 'auto'
        ? align
        : (anchorCenterX <= leftThreshold
            ? 'right'
            : (anchorCenterX >= rightThreshold ? 'left' : 'center'))
    setCurrentAlign(computedAlign)
    // Compute vertical placement (auto by anchor vertical position)
    const anchorCenterY = anchor.y + (anchor.height / 2)
    const computedVertical: 'above' | 'below' =
      vertical && vertical !== 'auto'
        ? vertical
        : (anchorCenterY <= (windowHeight / 2) ? 'below' : 'above')
    setCurrentVertical(computedVertical)
    setCurrentShowArrow(!!showArrow)
    setVisible(true)
  }, [defaultOffset, defaultAppearance, windowWidth, windowHeight])

  const contextValue = useMemo(() => ({ showFromAnchor, hide }), [showFromAnchor, hide])

  useEffect(() => {
    if (!visible || Platform.OS !== 'web') {
      if (mouseMoveHandlerRef.current) {
        // eslint-disable-next-line no-undef
        window.removeEventListener('mousemove', mouseMoveHandlerRef.current as any)
        mouseMoveHandlerRef.current = null
      }
      return
    }

    const handler = (e: any) => {
      const anchorRect = anchorRectRef.current
      const contentRect = contentRectRef.current
      if (!anchorRect && !contentRect) return
      const x = e?.clientX ?? 0
      const y = e?.clientY ?? 0
      const withinAnchor = anchorRect ? (
        x >= anchorRect.x - 10 &&
        x <= anchorRect.x + anchorRect.width + 10 &&
        y >= anchorRect.y - 10 &&
        y <= anchorRect.y + anchorRect.height + 10
      ) : false
      const withinContent = contentRect ? (
        x >= contentRect.x - 10 &&
        x <= contentRect.x + contentRect.width + 10 &&
        y >= contentRect.y - 10 &&
        y <= contentRect.y + contentRect.height + 10
      ) : false
      if (!(withinAnchor || withinContent)) {
        hide()
      }
    }
    mouseMoveHandlerRef.current = handler
    // eslint-disable-next-line no-undef
    window.addEventListener('mousemove', handler)

    return () => {
      if (mouseMoveHandlerRef.current) {
        // eslint-disable-next-line no-undef
        window.removeEventListener('mousemove', mouseMoveHandlerRef.current as any)
        mouseMoveHandlerRef.current = null
      }
    }
  }, [visible, hide])

  // Compute positioned style when we know anchor and content size
  const positionedStyle = useMemo(() => {
    if (!anchor) return { left: 0, top: 0 }

    const margin = 8
    const offset = currentOffset
    // ensure the arrow never overlaps the anchor; arrow extends ~6-7px from bubble
    const anchorGap = 8

    const availableWidth = windowWidth - insets.left - insets.right
    const availableHeight = windowHeight - insets.top - insets.bottom

    let left = anchor.x + offset.x
    // Compute positions that never overlap the anchor (account for arrow height)
    const belowTop = anchor.y + anchor.height + anchorGap + offset.y
    const aboveTop = anchor.y - contentSize.height - anchorGap - offset.y
    const fitsBelow = belowTop + contentSize.height + margin <= availableHeight
    const fitsAbove = aboveTop >= margin
    let top = currentVertical === 'below'
      ? (fitsBelow ? belowTop : (fitsAbove ? aboveTop : belowTop))
      : (fitsAbove ? aboveTop : (fitsBelow ? belowTop : aboveTop))

    // Horizontal alignment relative to anchor
    if (currentAlign === 'center') {
      left = anchor.x + ((anchor.width - contentSize.width) / 2) + offset.x
    } else if (currentAlign === 'right') {
      left = anchor.x + anchor.width - contentSize.width + offset.x
    }

    // If popover would overflow right edge, shift left
    if (left + contentSize.width + margin > availableWidth) {
      left = Math.max(margin, availableWidth - contentSize.width - margin)
    }

    // If still overflowing vertically, clamp within bounds while keeping gap from anchor
    if (top + contentSize.height + margin > availableHeight) {
      top = Math.max(margin, aboveTop)
    }

    // Clamp to screen with margins
    left = Math.max(margin, Math.min(left, availableWidth - contentSize.width - margin))
    top = Math.max(margin, Math.min(top, availableHeight - contentSize.height - margin))

    // Absolute positioning relative to screen (safe-area insets applied at measurement time)
    return { left: left, top: top}
  }, [
    anchor,
    contentSize.height,
    contentSize.width,
    insets.left,
    insets.right,
    insets.top,
    insets.bottom,
    windowHeight,
    windowWidth,
    currentOffset,
    currentAlign,
    currentVertical
  ])

  // Keep a ref of the current content rectangle (for web hover-retention)
  useEffect(() => {
    if (!anchor) {
      contentRectRef.current = null
      return
    }
    const left = (positionedStyle as { left?: number }).left ?? 0
    const top = (positionedStyle as { top?: number }).top ?? 0
    contentRectRef.current = {
      x: left,
      y: top,
      width: contentSize.width,
      height: contentSize.height
    }
  }, [anchor, positionedStyle, contentSize])

  return (
    <PopoverContext.Provider value={contextValue}>
      <View style={{ flex: 1 }}>
        {children}
        <Modal
          visible={visible && !!anchor}
          transparent
          animationType="none"
          statusBarTranslucent={Platform.OS === 'android'}
          navigationBarTranslucent={Platform.OS === 'android'}
          onRequestClose={hide}
        >
          <View pointerEvents="box-none" style={{ flex: 1 }}>
            <Pressable
              onPressIn={(e) => {
                const aRect = anchorRectRef.current
                const cRect = contentRectRef.current
                if (!aRect && !cRect) {
                  hide()
                  return
                }
                const ev = e as unknown as { nativeEvent?: { pageX?: number, pageY?: number } }
                const native = ev.nativeEvent || {}
                const pageX = native.pageX ?? 0
                const pageY = native.pageY ?? 0
                const withinAnchor = aRect ? (
                  pageX >= aRect.x - 10 && pageX <= aRect.x + aRect.width + 10 &&
                  pageY >= aRect.y - 10 && pageY <= aRect.y + aRect.height + 10
                ) : false
                const withinContent = cRect ? (
                  pageX >= cRect.x - 10 && pageX <= cRect.x + cRect.width + 10 &&
                  pageY >= cRect.y - 10 && pageY <= cRect.y + cRect.height + 10
                ) : false
                if (!(withinAnchor || withinContent)) hide()
              }}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
              }}
            />
            {anchor ? (
              <Pressable
                onPress={() => { /* swallow presses inside */ }}
                style={{ position: 'absolute', ...positionedStyle }}
              >
                {currentShowArrow ? (() => {
                  const bg = currentAppearance?.backgroundColor || theme.colors.bgSecond
                  const border = currentAppearance?.borderColor || theme.colors.borderColor
                  const borderW = currentAppearance?.borderWidth ?? 0
                  const hasBorder = borderW > 0
                  // Position arrow to point at the center of the anchor,
                  // independent of popover horizontal alignment.
                  const bubbleLeft = (positionedStyle as { left?: number }).left ?? 0
                  const anchorCenterX = (anchor.x + (anchor.width / 2))
                  // Arrow width is 12px (6+6), so subtract half (6) to center the tip
                  const idealArrowLeft = anchorCenterX - bubbleLeft - 6
                  // Keep a small margin so arrow isn't flush against bubble edges
                  const minArrowLeft = 10
                  const maxArrowLeft = Math.max(minArrowLeft, (contentSize.width - 16))
                  const arrowLeft = Math.max(minArrowLeft, Math.min(idealArrowLeft, maxArrowLeft))
                  const bubbleTop = (positionedStyle as { top?: number }).top ?? 0
                  const isAbove = bubbleTop <= (anchor.y - 1)
                  return (
                    <>
                      {(!isAbove) ? (
                        <>
                          {/* Border arrow (slightly larger) */}
                          {hasBorder ? (
                            <View
                              style={{
                                position: 'absolute',
                                top: -7,
                                left: arrowLeft - 1,
                                width: 0,
                                height: 0,
                                borderLeftWidth: 7,
                                borderRightWidth: 7,
                                borderBottomWidth: 7,
                                borderLeftColor: 'transparent',
                                borderRightColor: 'transparent',
                                borderBottomColor: border,
                              }}
                            />
                          ) : null}
                          {/* Fill arrow */}
                          <View
                            style={{
                              position: 'absolute',
                              top: -6,
                              left: arrowLeft,
                              width: 0,
                              height: 0,
                              borderLeftWidth: 6,
                              borderRightWidth: 6,
                              borderBottomWidth: 6,
                              borderLeftColor: 'transparent',
                              borderRightColor: 'transparent',
                              borderBottomColor: bg,
                            }}
                          />
                        </>
                      ) : (
                        <>
                          {/* Border arrow (slightly larger) - pointing up from bottom */}
                          {hasBorder ? (
                            <View
                              style={{
                                position: 'absolute',
                                bottom: -7,
                                left: arrowLeft - 1,
                                width: 0,
                                height: 0,
                                borderLeftWidth: 7,
                                borderRightWidth: 7,
                                borderTopWidth: 7,
                                borderLeftColor: 'transparent',
                                borderRightColor: 'transparent',
                                borderTopColor: border,
                              }}
                            />
                          ) : null}
                          {/* Fill arrow */}
                          <View
                            style={{
                              position: 'absolute',
                              bottom: -6,
                              left: arrowLeft,
                              width: 0,
                              height: 0,
                              borderLeftWidth: 6,
                              borderRightWidth: 6,
                              borderTopWidth: 6,
                              borderLeftColor: 'transparent',
                              borderRightColor: 'transparent',
                              borderTopColor: bg,
                            }}
                          />
                        </>
                      )}
                    </>
                  )
                })() : null}

                <View
                  onLayout={(e) => {
                    const { width, height } = e.nativeEvent.layout
                    if (width !== contentSize.width || height !== contentSize.height) {
                      setContentSize({ width, height })
                    }
                  }}
                  style={{
                    backgroundColor: currentAppearance?.backgroundColor || theme.colors.bgSecond,
                    borderRadius: 10,
                    padding: 8,
                    borderWidth: currentAppearance?.borderWidth ?? 0,
                    borderColor: currentAppearance?.borderColor ?? 'transparent',
                    // remove top border so the arrow seamlessly continues the outline
                    borderTopColor: 'transparent',
                    // prevent bubble from exceeding viewport width
                    maxWidth: Math.max(
                      100,
                      (windowWidth - insets.left - insets.right) - (8 * 2)
                    ),
                    // cap height so bubble never overlaps anchor; allow content to scroll if needed
                    maxHeight: (() => {
                      const availableHeight = windowHeight - insets.top - insets.bottom
                      const gapBelow = Math.max(0, availableHeight - (anchor.y + anchor.height))
                      const gapAbove = Math.max(0, anchor.y)
                      const bubbleTop = (positionedStyle as { top?: number }).top ?? 0
                      const isAbove = bubbleTop <= (anchor.y - 1)
                      const anchorGap = 8
                      const limit = (isAbove ? gapAbove : gapBelow) - anchorGap
                      return Math.max(80, limit)
                    })(),
                  }}
                >
                  {content}
                </View>
              </Pressable>
            ) : null}
          </View>
        </Modal>
      </View>
    </PopoverContext.Provider>
  )
}

export const usePopover = (): PopoverContextType => {
  const ctx = useContext(PopoverContext)
  if (!ctx) throw new Error('usePopover must be used within a PopoverProvider')
  return ctx
}

// Helper to measure a ref and open a popover
export const showPopoverFromRef = async (
  ref: React.RefObject<{
    measure?: (
      cb: (
        x: number,
        y: number,
        width: number,
        height: number,
        pageX: number,
        pageY: number
      ) => void
    ) => void
    measureInWindow: (
      cb: (x: number, y: number, width: number, height: number) => void
    ) => void
  }> ,
  showFromAnchor: PopoverContextType['showFromAnchor'],
  content: React.ReactNode,
  onClose?: () => void,
  options?: {
    offset?: number | { x: number, y: number },
    appearance?: PopoverAppearance,
    align?: 'left' | 'center' | 'right' | 'auto',
    vertical?: 'above' | 'below' | 'auto',
    showArrow?: boolean
  }
): Promise<void> => {
  if (!ref?.current) return
  const node = ref.current

  // Android modal/window coordinate handling is sensitive to system bars and edge-to-edge.
  // Prefer `pageX/pageY` from `measure()` there, with `measureInWindow()` as fallback.
  if (Platform.OS === 'android' && typeof node.measure === 'function') {
    node.measure((_, __, width, height, pageX, pageY) => {
      if (
        Number.isFinite(pageX) &&
        Number.isFinite(pageY) &&
        Number.isFinite(width) &&
        Number.isFinite(height)
      ) {
        showFromAnchor({
          anchor: { x: pageX, y: pageY, width, height },
          content,
          onClose,
          ...options
        })
        return
      }

      node.measureInWindow((x: number, y: number, w: number, h: number) => {
        showFromAnchor({ anchor: { x, y, width: w, height: h }, content, onClose, ...options })
      })
    })
    return
  }

  // iOS/web: use measureInWindow absolute coordinates.
  node.measureInWindow((x: number, y: number, width: number, height: number) => {
    showFromAnchor({ anchor: { x, y, width, height }, content, onClose, ...options })
  })
}

// Helper to open popover from a press event coordinates (pageX/pageY)
export const showPopoverFromPoint = (
  pageX: number,
  pageY: number,
  showFromAnchor: PopoverContextType['showFromAnchor'],
  content: React.ReactNode,
  onClose?: () => void,
  options?: { offset?: number | { x: number, y: number }, appearance?: PopoverAppearance }
): void => {
  const width = 1
  const height = 1
  showFromAnchor({ anchor: { x: pageX, y: pageY, width, height }, content, onClose, ...options })
}


